import { logUserEvent } from '@tailored-pdf/api';

/**
 * In-editor user actions. Logged under the ACTION log type, separately from EVENT
 * (funnel milestones) and ERROR. Mirrors the UserEventType enum in the OpenAPI spec.
 */
export type ActionEventType =
  | 'EDIT_TEXT'
  | 'ADD_TEXT'
  | 'DELETE_TEXT'
  | 'ADD_TEXT_FORM'
  | 'ADD_TEXTAREA_FORM'
  | 'ADD_SIGNATURE'
  | 'ADD_IMAGE'
  | 'ERASE'
  | 'DRAW_PEN'
  | 'DRAW_HIGHLIGHTER'
  | 'DRAW_RECTANGLE'
  | 'DRAW_CIRCLE'
  | 'DRAW_LINE'
  | 'DRAW_ARROW'
  | 'ADD_PAGE'
  | 'DELETE_PAGE';

type EventLoggerProps =
  | {
      logType: 'EVENT';
      eventType:
        | 'USER_SIGN_IN'
        | 'USER_SIGN_UP'
        | 'SUBSCRIPTION_MODAL_OPENED'
        | 'CHECKOUT_FORM_SUBMITTED'
        | 'CHECKOUT_LINK_FOLLOWED'
        | 'CHECKOUT_SUCCEEDED'
        | 'UPLOAD_FILE'
        | 'PROCESS_FILE'
        | 'GUEST_ID_ASSIGNED'
        | 'DOWNLOAD_FILE'
        | 'DOWNLOAD_FILE_AS_WORD'
        | 'DOWNLOAD_FILE_FROM_LIST'
        | 'UPLOAD_FILE_TO_CONVERT'
        | 'CONVERT_TO_WORD'
        | 'DOWNLOAD_CONVERTED_TO_WORD';
      trace?: unknown;
    }
  | {
      logType: 'ERROR';
      eventType:
        | 'USER_SIGN_IN_ERROR'
        | 'USER_SIGN_UP_ERROR'
        | 'CHECKOUT_ERROR'
        | 'UPLOAD_FILE_ERROR'
        | 'PROCESS_FILE_ERROR'
        | 'WRONG_FILE_TYPE_UPLOADED'
        | 'WRONG_FILE_SIZE_UPLOADED'
        | 'DOWNLOAD_FILE_ERROR'
        | 'UPLOAD_FILE_TO_CONVERT_ERROR'
        | 'CONVERT_TO_WORD_ERROR'
        | 'DOWNLOAD_CONVERTED_TO_WORD_ERROR';
      trace: unknown;
    }
  | {
      logType: 'ACTION';
      eventType: ActionEventType;
      trace?: unknown;
    };

export function fireProcessFileConversion() {
  if (window.gtag) {
    // eslint-disable-next-line no-console
    console.log('fire GoogleAds conversion_event_page_view');
    window.gtag('event', 'conversion_event_page_view', {});
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'conversion_event_page_view',
  });
}

export function fireSignupConversion() {
  if (window.gtag) {
    // eslint-disable-next-line no-console
    console.log('fire GoogleAds conversion_event_signup');
    window.gtag('event', 'conversion_event_signup', {});
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'conversion_event_signup',
  });
}

export function fireSubscribedConversion(email?: string) {
  const transactionId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  // Enhanced Conversions: attach the subscriber's email so Google can match the
  // conversion to the ad click server-side when the gclid cookie is missing (iOS ITP,
  // cross-device, or lost across the payment redirect). ~50% of paid subscribers arrive
  // without a usable gclid, and without this they go unattributed. gtag hashes the email
  // client-side (SHA-256) over HTTPS. We also mirror it into the dataLayer push so a
  // GTM-based Enhanced Conversions setup can read it. No email -> no user data (safe no-op).
  const userData = email?.trim() ? { email: email.trim() } : undefined;

  if (window.gtag) {
    // eslint-disable-next-line no-console
    console.log('fire GoogleAds conversion_event_subscribe_paid');
    if (userData) {
      window.gtag('set', 'user_data', userData);
    }
    window.gtag('event', 'conversion_event_subscribe_paid', {
      value: 0.95,
      currency: 'USD',
      transaction_id: transactionId,
    });
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'conversion_event_subscribe_paid',
    value: 0.95,
    currency: 'USD',
    transaction_id: transactionId,
    ...(userData && { user_data: userData }),
  });
}

export function logEvent({ eventType, logType, trace = '' }: EventLoggerProps) {
  try {
    const message =
      typeof trace === 'string' ? trace : typeof trace === 'object' ? JSON.stringify(trace) : String(trace);

    const referer = document.referrer || undefined
    const page = typeof window !== 'undefined' ? window.location.href : undefined;

    // Analytics is best-effort and deliberately not awaited, so the promise needs its
    // own handler — the try/catch below only guards the synchronous work above. Without
    // it every beacon dropped by a flaky network became an unhandled rejection and, via
    // Sentry's global handler, a bogus "AxiosError: Network Error" report.
    logUserEvent(message, { logType, eventType, referer, page }).catch(() => undefined);
  } catch (_ignore) {
    //nothing to do
  }
}
